import {
  ConditionalExpression,
  OPERATORS,
} from '@jetfabric/schemas/lib/protocol-sdk-api-v2/query/condition';
import {
  isDataReference,
  isEvaluateableExpression,
  isLet,
  isVariableReference,
  validOperatorHeads,
} from './guards';

const typesMatch = (value: any, expectedType: ArgType) => {
  const valueType: string = typeof value;
  if (Array.isArray(expectedType.acceptableTypes)) {
    return expectedType.acceptableTypes.includes(valueType as BasicJSONType);
  }
  return valueType === expectedType.acceptableTypes;
};

export const typeCheck = (operator: OPERATORS, args: ConditionalExpression[]): boolean => {
  if (!validOperatorHeads.has(operator)) {
    throw new Error(`typeCheck: Unknown operator ${operator}`);
  }
  if (!Array.isArray(args)) {
    throw new Error(`typeCheck: args should be array, got ${typeof args} instead.`);
  }
  const argTypes = operatorArgTypes[operator];
  if (!argTypes) {
    return true; // no arg type information
  }
  let allChecked = true;
  for (let i = 0; i < argTypes.length; i++) {
    // references and unevaluated expressions cannot be type checked
    if (
      isEvaluateableExpression(args[i]) ||
      isLet(args[i]) ||
      isDataReference(args[i]) ||
      isVariableReference(args[i])
    ) {
      allChecked = false;
      continue;
    }
    const value: ConditionalExpression = args[i];
    const expectedType = argTypes[i];
    if (!typesMatch(value, expectedType)) {
      throw new Error(
        `typeCheck: operator ${operator} argument # ${i} should be ${JSON.stringify(
          expectedType.acceptableTypes
        )}, instead found ${typeof value}`
      );
    }
    if (expectedType.additionalCheck && !expectedType.additionalCheck(value)) {
      throw new Error(
        `typeCheck: operator ${operator} argument # ${i} failed to pass additionalCheck()`
      );
    }
  }
  return allChecked;
};

type BasicJSONType = 'any' | 'number' | 'boolean' | 'string' | 'undefined' | 'null' | 'object';

interface ArgType {
  acceptableTypes: BasicJSONType | BasicJSONType[];
  additionalCheck?: (arg: any) => boolean;
}

const numericVarArgs: ArgType[] = [
  { acceptableTypes: 'number' },
  { acceptableTypes: 'number' },
  // additional arguments optional
  { acceptableTypes: ['number', 'undefined'] },
  { acceptableTypes: ['number', 'undefined'] },
  { acceptableTypes: ['number', 'undefined'] },
  // NOTE: no type checking for 5+ arguments to sum
];

export const operatorArgTypes: { [key in OPERATORS]?: ArgType[] } = {
  [OPERATORS.SUM]: numericVarArgs,
  [OPERATORS.MINUS]: [{ acceptableTypes: 'number' }, { acceptableTypes: ['number', 'undefined'] }],
  [OPERATORS.PRODUCT]: numericVarArgs,
  [OPERATORS.DIVIDE]: [{ acceptableTypes: 'number' }, { acceptableTypes: 'number' }],
  [OPERATORS.LT]: [{ acceptableTypes: 'number' }, { acceptableTypes: 'number' }],
  [OPERATORS.IF]: [], // no type checks, as arguments can have any type
  [OPERATORS.GT]: [{ acceptableTypes: 'number' }, { acceptableTypes: 'number' }],
  [OPERATORS.REGEXP]: [
    { acceptableTypes: 'string' },
    { acceptableTypes: 'string' },
    { acceptableTypes: ['string', 'undefined'] },
  ],
  [OPERATORS.IN]: [
    { acceptableTypes: ['number', 'string', 'boolean'] },
    { acceptableTypes: 'object', additionalCheck: Array.isArray },
  ],
  [OPERATORS.INTERSECT]: [
    { acceptableTypes: 'object', additionalCheck: Array.isArray },
    { acceptableTypes: 'object', additionalCheck: Array.isArray },
  ],
  [OPERATORS.PATH]: [{ acceptableTypes: 'string' }, { acceptableTypes: 'object' }],
};

interface Arity {
  min: number;
  max?: number;
}

const argCount = (min = 1, max?: number): Arity => {
  const arity: Arity = { min };
  if (typeof max === 'number') {
    arity.max = max;
  }
  return arity;
};

export const operatorArities: { [key in OPERATORS]: Arity } = {
  [OPERATORS.AND]: argCount(2),
  [OPERATORS.OR]: argCount(2),
  [OPERATORS.NOT]: argCount(1, 1),
  [OPERATORS.EQ]: argCount(2),
  [OPERATORS.LT]: argCount(2, 2),
  [OPERATORS.GT]: argCount(2, 2),
  [OPERATORS.IF]: argCount(3, 3),
  [OPERATORS.REGEXP]: argCount(2, 3),
  [OPERATORS.IN]: argCount(2, 2),
  [OPERATORS.INTERSECT]: argCount(2, 2),
  [OPERATORS.SUM]: argCount(2),
  [OPERATORS.MINUS]: argCount(1, 2),
  [OPERATORS.PRODUCT]: argCount(2),
  [OPERATORS.DIVIDE]: argCount(2, 2),
  [OPERATORS.PATH]: argCount(2, 3),
  [OPERATORS.TRACE]: argCount(1),
  [OPERATORS.TRACE_EVAL]: argCount(1),
};

export const arityCheck = (operator: OPERATORS, args: ConditionalExpression[]): void => {
  if (!validOperatorHeads.has(operator)) {
    throw new Error(`arityCheck: Unknown operator "${operator}"`);
  }
  if (!Array.isArray(args)) {
    throw new Error(`arityCheck: args should be array, got ${typeof args} instead.`);
  }
  const arity = operatorArities[operator];
  if (!arity) {
    return; // no arity information, but this shouldn't be allowed by tsc
  }
  if (args.length < arity.min) {
    throw new Error(
      `arityCheck: operator ${operator} requires at least ${arity.min} arguments, received ${args.length}`
    );
  }
  if (typeof arity.max === 'number' && args.length > arity.max) {
    throw new Error(
      `arityCheck: operator ${operator} requires at most ${arity.max} arguments, received ${args.length}`
    );
  }
};
